import { useState, useEffect, useRef, useCallback } from 'react';
import { getFlowEnvironment, isEntitlementDenied } from '../../../services/analytics.service';
import { normalizeAnalyticsError } from '../shared/analyticsViewUtils';

/**
 * Custom hook to fetch and cache site-level client environment telemetry (devices, browsers, OS, locations).
 * Supports global refresh triggers and handles single-day date ranges seamlessly.
 *
 * @param {Object} filters - Filter parameters.
 * @param {string} filters.organizationId - Organization GUID.
 * @param {string} filters.siteCollectionId - Site collection GUID.
 * @param {string} [filters.startDate] - ISO-8601 start date.
 * @param {string} [filters.endDate] - ISO-8601 end date.
 * @param {string} [filters.fromUtc] - Alternative ISO-8601 start date.
 * @param {string} [filters.toUtc] - Alternative ISO-8601 end date.
 * @param {number|string} [filters.refreshKey] - Incrementing trigger to bypass cache and force refetch.
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Whether query should execute.
 * @returns {Object} { data, loading, error, isEntitled, entitlementDenied, refetch }
 */
export function useFlowEnvironment(filters = {}, options = {}) {
  const { organizationId, siteCollectionId, refreshKey } = filters;
  let startDate = filters.fromUtc || filters.startDate;
  let endDate = filters.toUtc || filters.endDate;

  if (startDate && typeof startDate === 'string' && !startDate.includes('T')) {
    startDate = `${startDate}T00:00:00Z`;
  }
  if (endDate && typeof endDate === 'string' && !endDate.includes('T')) {
    endDate = `${endDate}T23:59:59Z`;
  }

  if (startDate && endDate && new Date(startDate).getTime() >= new Date(endDate).getTime()) {
    const d = new Date(startDate);
    d.setUTCHours(23, 59, 59, 999);
    endDate = d.toISOString();
  }

  const enabled = options.enabled !== false;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entitlementDenied, setEntitlementDenied] = useState(false);

  // In-memory cache keyed by "org:site:start:end"
  const cacheRef = useRef(new Map());
  const activeRequestKeyRef = useRef(null);
  const prevRefreshKeyRef = useRef(refreshKey);

  const queryKey = (organizationId && siteCollectionId && startDate && endDate)
    ? `${organizationId}:${siteCollectionId}:${startDate}:${endDate}`
    : null;

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!enabled || !queryKey) {
        return;
      }

      if (!forceRefresh && cacheRef.current.has(queryKey)) {
        const cached = cacheRef.current.get(queryKey);
        setData(cached);
        setError(null);
        setEntitlementDenied(false);
        setLoading(false);
        return;
      }

      activeRequestKeyRef.current = queryKey;
      setLoading(true);
      setError(null);
      setEntitlementDenied(false);

      try {
        const result = await getFlowEnvironment({
          organizationId,
          siteCollectionId,
          startDate,
          endDate
        });

        if (activeRequestKeyRef.current === queryKey) {
          cacheRef.current.set(queryKey, result);
          setData(result);
          setError(null);
          setEntitlementDenied(false);
        }
      } catch (err) {
        if (activeRequestKeyRef.current === queryKey) {
          const denied = isEntitlementDenied(err);
          setEntitlementDenied(denied);
          const message = normalizeAnalyticsError(err, 'Failed to fetch environment analytics data');
          setError(message);
        }
      } finally {
        if (activeRequestKeyRef.current === queryKey) {
          setLoading(false);
        }
      }
    },
    [enabled, queryKey, organizationId, siteCollectionId, startDate, endDate]
  );

  useEffect(() => {
    if (prevRefreshKeyRef.current !== refreshKey) {
      prevRefreshKeyRef.current = refreshKey;
      fetchData(true);
    } else {
      fetchData(false);
    }
  }, [fetchData, refreshKey]);

  const refetch = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isEntitled: !entitlementDenied,
    entitlementDenied,
    refetch
  };
}

export default useFlowEnvironment;
